/**
 * ProposalsCard — Customer 360 Sprint (Day 4)
 *
 * Inline card on CustomerDetailModal's Info tab. Shows signed proposals
 * pulled from the customer's deals — accepted item counts + one-time +
 * recurring totals, plus a link to each customer-facing public viewer.
 *
 * Loading vs empty discipline: when `loading` is true, render a skeleton
 * (three placeholder rows). Only show "No signed proposals yet" when
 * loading has finished AND the list is empty. Empty-state-vs-loading-state
 * confusion is the #1 source of dashboard "is it broken?" tickets.
 */

import { getLocaleSync } from '@/utils/locale';
import { motion } from 'framer-motion';
import { FileSignature, ExternalLink } from 'lucide-react';
import { Timestamp } from 'firebase/firestore';
import type { SignedProposalSummary } from '@/services/aggregate-customer.service';
import { formatCurrency } from '@/utils/pricing';

/**
 * `deal.proposalSignedAt` is typed Timestamp but at runtime can be either a
 * Firestore Timestamp object OR an ISO string (BP webhooks vs migrations
 * write different shapes). Normalise to a localised display string here so
 * the card never renders "Invalid Date". Mirrors the pattern in
 * ProposalStatusCard.tsx:37-47.
 */
function formatSignedDate(value: string | Timestamp | null | undefined): string {
  if (!value) return '';
  let date: Date;
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    date = value.toDate();
  } else {
    date = new Date(String(value));
  }
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString(getLocaleSync(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

interface ProposalsCardProps {
  proposals: SignedProposalSummary[];
  loading: boolean;
}

export function ProposalsCard({ proposals, loading }: ProposalsCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-surface-card border border-theme rounded-xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <FileSignature className="w-4 h-4 text-brand-secondary" />
        <h3 className="text-sm font-bold text-theme-primary">Signed proposals</h3>
        {!loading && proposals.length > 0 && (
          <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-brand-secondary/10 text-brand-secondary">
            {proposals.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2" aria-label="Loading signed proposals">
          <div className="h-9 rounded-lg bg-surface-hover animate-pulse" />
          <div className="h-9 rounded-lg bg-surface-hover animate-pulse" />
          <div className="h-9 rounded-lg bg-surface-hover animate-pulse" />
        </div>
      ) : proposals.length === 0 ? (
        <p className="text-xs text-theme-tertiary">No signed proposals yet.</p>
      ) : (
        <ul className="space-y-1.5">
          {proposals.map((p) => (
            <li
              key={p.dealId}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-surface-hover transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-theme-primary truncate">
                  {p.dealTitle}
                </p>
                <p className="text-[10px] text-theme-tertiary flex items-center gap-1.5">
                  {p.acceptedItemCount > 0 && (
                    <span>
                      {p.acceptedItemCount} item{p.acceptedItemCount === 1 ? '' : 's'}
                    </span>
                  )}
                  {p.oneTimeValue > 0 && (
                    <span>· {formatCurrency(p.oneTimeValue)}</span>
                  )}
                  {p.monthlyRecurring > 0 && (
                    <span>+ {formatCurrency(p.monthlyRecurring)}/mo</span>
                  )}
                  {formatSignedDate(p.signedAt) && (
                    <span>· signed {formatSignedDate(p.signedAt)}</span>
                  )}
                </p>
              </div>
              {p.proposalUrl && (
                <a
                  href={p.proposalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title="Open customer-facing proposal in a new tab"
                  aria-label={`Open ${p.dealTitle} proposal in a new tab`}
                  className="p-1.5 rounded-md hover:bg-brand-secondary/10 text-theme-tertiary hover:text-brand-secondary transition-colors flex-shrink-0"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </motion.div>
  );
}
